/* ============
 * Clients Index
 * ============
 *
 * This page shows the list of clients and client groups.
 */

import { mapGetters, mapMutations } from 'vuex';
import debounce from 'debounce';
import clientService from './../../../../services/client';
import accountService from './../../../../services/account';
import store from './../../../../store';
import SearchMixin from './../../../../mixins/search';
import moment from 'moment';
import clientImportExportPopup from '../../../../components/clientImportExportPopup/clientImportExportPopup.vue';
import bulkProcessClientModal from '../../../../components/bulkProcessClientModal/bulkProcessClientModal.vue';

export default {
  data() {
    return {
      loading: true,
      sort: 'bestPaying',
      info: 'info',
      primary: 'primary',
      search: '',
      sortTypes: [{
        id: 'bestPaying',
        name: this.$t('sort.bestPaying'),
      },
      {
        id: 'worstPaying',
        name: this.$t('sort.worstPaying'),
      },
      {
        id: 'biggestDebtors',
        name: this.$t('sort.biggestDebtors'),
      },
      {
        id: 'name|asc',
        name: `${this.$t('sort.name')} (A -> Z)`,
      },
      {
        id: 'name|desc',
        name: `${this.$t('sort.name')} (Z -> A)`,
      }],
      limit: 25,
      multipleSelection: [],
      bulkSelectionLimit: 25,
      showAction: false,
    };
  },

  computed: {
    ...mapGetters({
      clients: 'clients',
      view: 'getView',
      searchQuery: 'clientQuery',
      account: 'account',
    }),
    hasEHFModule() {
      if (this.account && this.account.modules && this.account.modules.length > 0) {
        return !!this.account.modules.find(module => module.name === 'EHF');
      }
      return false;
    },
    clientsListView() {
      return this.clients.items.map(c => ({
        name: c.name,
        organizationNumber: c.organizationNumber || '–',
        phone: ((c.phoneNumber ? `tel: ${c.phoneNumber}` : '') + (c.mobileNumber ? ` mob: ${c.mobileNumber}` : '')),
        email: c.email,
        custNo: c.custNo || '-',
        ordersCount: c.ordersCount,
        createdAt: `${moment(c.createdAt).format('DD-MM-YYYY')}`,
        id: c.id,
        billMainParent: c.billMainParent === 1,
        clientParentId: c.clientParentId,
        clientParent: c.clientParent,
        ehfBilling3_0: !!c.ehfBilling3_0,
        ehfSort: c.ehfBilling3_0 ? 1 : 0,
      }));
    },

    selectedCount() {
      return this.multipleSelection.length || 0;
    },
  },

  watch: {

    view(view) {
      this.showAction = false;
      if (view == 'card') {
        this.info = 'info';
        this.primary = 'primary';
      } else {
        this.primary = 'info';
        this.info = 'primary';
      }
    },

    multipleSelection(multipleSelection) {
      this.showAction = false;
      if (multipleSelection.length) {
        this.showAction = true;
      }
    },

    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      this.multipleSelection = [];
      this.all(this.searchQuery.page, this.limit);
    },
  },

  mounted() {
    accountService.rememberPerPage('get', { from: 'client' })
      .then((data) => {
        this.limit = data.size;
        this.all(this.searchQuery.page, this.limit);
      }).catch((error) => {
        console.log(error);
      });

    this.setInnerSidebar([{
      title: this.$t('layouts.sidebar.clients'),
      options: [{
        title: this.$t('layouts.sidebar.active'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'active',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.inActive'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'inActive',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.orderedPastWeek'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'orderedPastWeek',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.orderedPastMonth'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'orderedPastMonth',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.neverOrdered'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'neverOrdered',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.storeSynced'),
        route: {
          name: 'clients.index',
          query: {
            filter: 'storeSynced',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.all'),
        route: {
          name: 'clients.index',
        },
      },
      ],
      button: {
        title: this.$t('button.addClient'),
        route: {
          name: 'clients.create',
        },
      },
    },
    {
      title: this.$t('layouts.sidebar.clientGroup'),
      options: [{
        title: this.$t('layouts.sidebar.active'),
        route: {
          name: 'clientGroups.index',
          query: {
            filter: 'active',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.inActive'),
        route: {
          name: 'clientGroups.index',
          query: {
            filter: 'inActive',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.newOrders'),
        route: {
          name: 'clientGroups.index',
          query: {
            filter: 'new',
          },
        },
      },
      {
        title: this.$t('layouts.sidebar.all'),
        route: {
          name: 'clientGroups.index',
        },
      },
      ],
      button: {
        title: this.$t('button.addClientGroup'),
        route: {
          name: 'clientGroups.create',
        },
      },
    },
    {
      title: this.$t('layouts.sidebar.clientAgreements'),
      options: [
        {
          title: this.$t('layouts.sidebar.active'),
          route: {
            name: 'pricingAgreements.index',
            query: { filter: 'active' },
          },
        },
        {
          title: this.$t('layouts.sidebar.inActive'),
          route: {
            name: 'pricingAgreements.index',
            query: { filter: 'inactive' },
          },
        },
        {
          title: this.$t('layouts.sidebar.all'),
          route: {
            name: 'pricingAgreements.index',
            query: { filter: 'all' },
          },
        },
        {
          title: this.$t('layouts.sidebar.importAgreement'),
          route: {
            name: 'pricingAgreements.index',
            query: { import: '1' },
          },
        },
      ],
      button: {
        title: this.$t('layouts.sidebar.newAgreement'),
        route: { name: 'pricingAgreements.create' },
      },
    },
    ]);

    if (this.view == 'card') {
      this.info = 'info';
      this.primary = 'primary';
    } else {
      this.primary = 'info';
      this.info = 'primary';
    }
  },

  destroyed() {
    this.unsetInnerSidebar();
    store.dispatch('resetClients');
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalSize: 'SET_MODAL_SIZE',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    onSelectAction(value) {
      if (this.multipleSelection.length > this.bulkSelectionLimit) {
        const message = `${this.$t('static.clients.index.bulkMaxLimitExceededError')} ${this.bulkSelectionLimit}`;
        this.$message({
          showClose: true,
          message,
          customClass: 'error',
        });
        return;
      }
      if (value) {
        this.setModalContent(() => bulkProcessClientModal);
        this.setModalProps({
          selectedClients: this.multipleSelection,
          onSubmit: this.handleBulkClientActionSubmit,
          type: value,
        });
        this.setModalCloseOnClick(false);
        this.setModalShowClose(false);
        this.setModalCloseOnPressEscape(false);
        this.toggleModal(true);
      }
    },

    handleBulkClientActionSubmit() {
      this.multipleSelection = [];
      this.all(1, 25);
    },

    handleSelectionChange(val) {
      this.multipleSelection = val;
    },

    showImportDownloadModal() {
      this.setModalContent(() => clientImportExportPopup);
      this.setModalSize('full');
      this.toggleModal(true);
    },

    perPage(size) {
      accountService.rememberPerPage('post', { from: 'client', perPage: size })
        .then((data) => {
          this.limit = data.size;
        }).catch((error) => {
          console.log(error);
        });
    },

    /**
     * Fetches paginated clients.
     *
     * @param  {int} page The page number.
     */
    all: debounce(function (page, limit) {
      this.loading = true;
      const query = { page, limit, search: this.searchQuery.search };
      store.dispatch('setClientQuery', query);
      this.search = this.searchQuery.search;
      clientService.items({
        page,
        limit,
        sort: this.sort,
        search: this.searchQuery.search,
        filter: this.$route.query.filter,
      }).then(() => {
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    }, 800),

    changeView(view) {
      if (this.info != view) {
        this.primary = this.info;
        this.info = view;
        if (this.info == 'info') {
          store.dispatch('setView', 'card');
        } else {
          store.dispatch('setView', 'list');
        }
      }
    },
    onChangeSearch(name, value) {
      store.dispatch('setClientQuery', { page: 1, limit: this.limit, search: value });
      this.all(1, this.limit);
    },
    /**
     * Delete specific client
     *
     * @param  {Object} client The client object
     */
    deleteClient(client) {
      this.$confirm(this.$t('static.clients.index.confirmDelete'), this.$t('static.clients.index.confirmDeleteTitle'), {
        confirmButtonText: this.$t('button.delete'),
        cancelButtonText: this.$t('button.cancel'),
        type: 'warning',
      }).then(() => {
        clientService.destroy(client.id);
      }).catch(() => {});
    },

    /**
     * Go to edit view for client group
     *
     * @param  {Object} client The client object
     */
    editClient(client) {
      this.$router.push({
        name: 'clients.edit',
        params: { id: client.id },
      });
    },

    /**
     * Go to edit view for client new tab
     *
     * @param  {Object} client The client object
     */
    editClientNewTab(client) {
      const route = this.$router.resolve({
        name: 'clients.edit',
        params: { id: client.id },
      });
      window.open(route.href, '_blank');
    },

    getInitials(client) {
      const name = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`;
      const words = name.trim().split(/\s+/).filter(Boolean);
      if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
      return words[0] ? words[0].slice(0, 2).toUpperCase() : '?';
    },

    getAvatarColor(client) {
      const name = client.companyName || `${client.firstName || ''} ${client.lastName || ''}`;
      const colors = ['#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },
  },

  mixins: [
    SearchMixin,
  ],

  components: {
    VCard: require('./../../../../components/card/card.vue'),
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VSelect: require('./../../../../components/form/select/select.vue'),
    VNoData: require('./../../../../components/noData/noData.vue'),
    VTable: require('./../../../../components/table/table.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/clients/index/index.js